/**
 * Ein Eingabefeld zeichnen — gemeinsam für Tauchgangsformular und
 * Sammelformular.
 *
 * Herausgelöst, damit die beiden Formulare nicht auseinanderlaufen: Sonst
 * bekäme das eine eine Vorschlagsliste und das andere nicht.
 */

import { wort } from '../format.js';

/**
 * Welche Tastatur das Telefon aufschlagen soll — als reine Entscheidung,
 * damit sie ohne DOM pruefbar ist (dieselbe Trennung wie in
 * `sammelformular.js:eingabenAusListe`).
 *
 * `type="number"` allein laesst die Wahl dem Browser; iOS zeigt dann einen
 * Ziffernblock ohne Dezimaltrenner. Ein Feld mit Bruchschritt braucht ihn —
 * 41.2 Meter Maximaltiefe sind der Normalfall, nicht die Ausnahme.
 *
 * Ein Name ist kein Satz: Die Autokorrektur macht aus einem Riffnamen sonst
 * ein Wort, das sie kennt, und schreibt Buddys klein weiter. Die Notiz ist
 * umgekehrt Fliesstext und behaelt beides.
 */
export function tastatur(f) {
  const t = {};
  if (f.art === 'zahl') t.inputMode = f.schritt && f.schritt < 1 ? 'decimal' : 'numeric';
  if (f.eigenname) {
    t.autocapitalize = 'words';
    t.autocorrect = 'off';
    t.spellcheck = false;
  } else if (f.art === 'mehrzeilig') {
    t.autocapitalize = 'sentences';
  }
  return t;
}

/** Ein Eingabefeld nach seiner Definition. */
export function eingabefeld(f, wert, vorschlaege) {
  const huelle = document.createElement('div');
  huelle.className = `feld feld-${f.art}`;
  const kennung = `f-${f.schluessel}`;

  const beschriftung = document.createElement('label');
  beschriftung.htmlFor = kennung;
  beschriftung.textContent = f.einheit ? `${f.beschriftung} (${f.einheit})` : f.beschriftung;
  huelle.append(beschriftung);

  let eingabe;
  if (f.art === 'auswahl') {
    eingabe = document.createElement('select');
    eingabe.append(new Option('—', ''));
    for (const w of f.werte) eingabe.append(new Option(wort(w), w));
  } else if (f.art === 'mehrzeilig') {
    eingabe = document.createElement('textarea');
    eingabe.rows = 4;
  } else {
    eingabe = document.createElement('input');
    eingabe.type = { datum: 'date', zeit: 'time', zahl: 'number' }[f.art] ?? 'text';
    if (f.schritt) eingabe.step = String(f.schritt);
    if (f.min !== undefined) eingabe.min = String(f.min);
    if (f.max !== undefined) eingabe.max = String(f.max);
  }
  eingabe.id = kennung;
  eingabe.name = f.schluessel;
  eingabe.value = wert ?? '';

  // Vorschlagsliste für Plätze, Reisen, Buddys und Wetter: hilft beim
  // Zusammenhalten der Schreibweisen, ohne neue Werte zu verbieten.
  if (vorschlaege?.length) {
    const liste = document.createElement('datalist');
    liste.id = `${kennung}-vorschlaege`;
    for (const v of vorschlaege) liste.append(new Option(v));
    eingabe.setAttribute('list', liste.id);
    huelle.append(liste);
  }

  const { inputMode, spellcheck, ...marken } = tastatur(f);
  if (inputMode) eingabe.inputMode = inputMode;
  if (spellcheck !== undefined) eingabe.spellcheck = spellcheck;
  for (const [name, wert] of Object.entries(marken)) eingabe.setAttribute(name, wert);

  huelle.append(eingabe);
  const meldung = document.createElement('p');
  meldung.className = 'feldmeldung';
  huelle.append(meldung);
  return huelle;
}
